import React from 'react';
import { MapPin, Map, Building, Kanban } from 'lucide-react';

/**
 * Компонент информации о маршруте для калькулятора расхода топлива
 */
const RouteParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  if (!formData.includeRouteInfo) {
    return (
      <div className="mb-6">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeRouteInfo"
            checked={formData.includeRouteInfo}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Добавить информацию о маршруте
          </span>
        </label>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-3">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeRouteInfo"
            checked={formData.includeRouteInfo}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white flex items-center">
            <Map className="w-5 h-5 mr-1 text-blue-500" />
            Информация о маршруте
          </span>
        </label>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Город отправления */}
          <div>
            <label htmlFor="routeInfo.startCity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <MapPin className="w-4 h-4 mr-2 text-blue-500" />
              Город отправления
            </label>
            <input
              type="text"
              id="routeInfo.startCity"
              name="routeInfo.startCity"
              value={formData.routeInfo.startCity}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['routeInfo.startCity'] ? 'input-error' : ''}`}
              placeholder="Например: Москва"
            />
            {errors['routeInfo.startCity'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['routeInfo.startCity']}
              </p>
            )}
          </div>

          {/* Город назначения */}
          <div>
            <label htmlFor="routeInfo.endCity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <MapPin className="w-4 h-4 mr-2 text-blue-500" />
              Город назначения
            </label>
            <input
              type="text"
              id="routeInfo.endCity"
              name="routeInfo.endCity"
              value={formData.routeInfo.endCity}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['routeInfo.endCity'] ? 'input-error' : ''}`}
              placeholder="Например: Санкт-Петербург"
            />
            {errors['routeInfo.endCity'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['routeInfo.endCity']}
              </p>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-700 p-3 rounded-lg border border-blue-100 dark:border-blue-800 mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Распределение типов дорог (%)
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Процент трассы */}
            <div>
              <label htmlFor="routeInfo.highwayPercentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Kanban className="w-4 h-4 mr-2 text-blue-500" />
                Трасса (%)
              </label>
              <input
                type="range"
                id="routeInfo.highwayPercentage"
                name="routeInfo.highwayPercentage"
                value={formData.routeInfo.highwayPercentage}
                onChange={(e) => {
                  handleInputChange(e);
                  // Обновляем процент города для сохранения суммы в 100%
                  const cityPercentage = 100 - parseInt(e.target.value, 10);
                  handleInputChange({
                    target: {
                      name: 'routeInfo.cityPercentage',
                      value: String(cityPercentage)
                    }
                  });
                }}
                min="0"
                max="100"
                step="5"
                className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">0%</span>
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{formData.routeInfo.highwayPercentage}%</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">100%</span>
              </div>
              {errors['routeInfo.highwayPercentage'] && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors['routeInfo.highwayPercentage']}
                </p>
              )}
            </div>

            {/* Процент города */}
            <div>
              <label htmlFor="routeInfo.cityPercentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Building className="w-4 h-4 mr-2 text-blue-500" />
                Город (%)
              </label>
              <input
                type="range"
                id="routeInfo.cityPercentage"
                name="routeInfo.cityPercentage"
                value={formData.routeInfo.cityPercentage}
                onChange={(e) => {
                  handleInputChange(e);
                  // Обновляем процент трассы для сохранения суммы в 100%
                  const highwayPercentage = 100 - parseInt(e.target.value, 10);
                  handleInputChange({
                    target: {
                      name: 'routeInfo.highwayPercentage',
                      value: String(highwayPercentage)
                    }
                  });
                }}
                min="0"
                max="100"
                step="5"
                className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">0%</span>
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{formData.routeInfo.cityPercentage}%</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">100%</span>
              </div>
              {errors['routeInfo.cityPercentage'] && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors['routeInfo.cityPercentage']}
                </p>
              )}
            </div>
          </div>
        </div>
        
        <div className="text-xs text-blue-600 dark:text-blue-400 mt-2 flex items-start">
          <Map className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
          <p>
            Распределение типов дорог влияет на расчёт расхода топлива. В городе расход в среднем на 25-35% выше, чем на трассе 
            из-за частых остановок, работы двигателя на холостых оборотах и низкой средней скорости. По умолчанию расчёт 
            предполагает 30% городского режима и 70% движения по трассе.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RouteParametersSection;